import React, { useEffect, useRef, memo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';

// Memoized MathRenderer with performance optimizations
const MathRenderer = memo(({ content, variant = "body2", sx = {} }) => {
  const containerRef = useRef(null);
  const lastContentRef = useRef('');

  // Escape HTML to prevent XSS when rendering with dangerouslySetInnerHTML (allowlist: only our math/display spans)
  const escapeHtml = useCallback((s) => {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }, []);

  // Process content to properly format LaTeX expressions; output is safe for dangerouslySetInnerHTML (escaped + controlled tags)
  const processContent = useCallback((text) => {
    if (!text) return '';
    const debug = typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_MATH_RENDERER') === '1';
    let t = escapeHtml(String(text));
    // Normalize Unicode backslash/dollar to ASCII so LaTeX is detected and MathJax can render (e.g. when pasted or from some inputs)
    t = t
      .replace(/\u2216/g, '\\')  // U+2216 SET MINUS (often used as backslash)
      .replace(/\uFF3C/g, '\\')  // U+FF3C FULLWIDTH REVERSE SOLIDUS
      .replace(/\uFF04/g, '$');  // U+FF04 FULLWIDTH DOLLAR SIGN
    // Recover common power-template edits where the exponent was typed after the
    // closing delimiter: "x^{}$2" -> "x^{2}$".
    t = t.replace(/([A-Za-z0-9])\^\{\}\$([A-Za-z0-9]+)/g, '$1^{$2}$');
    // Recover adjacent plain exponent/subscript formulas that lost the opening delimiter,
    // e.g. "$x^{3}$x^{2}$" -> "$x^{3}$$x^{2}$".
    t = t.replace(/(\$[^\n$]+\$)([A-Za-z][A-Za-z0-9{}\\^_+\-*/=(). ]*[\\^_][A-Za-z0-9{}\\^_+\-*/=(). ]*)\$/g, '$1$$$2$');
    // Only treat $...$ as inline math when content looks like LaTeX (has \ or ^ or _), so currency like $4 and $7 stays plain.
    // Content ending with a single \ is likely escaped currency (e.g. "4 and \" before the next $), not LaTeX.
    const looksLikeLatex = (inner) => /[\\^_]/.test(inner) && !/\\$/.test(inner.trim());
    // Currency: $ followed by digits (optional comma/dot) with no closing $ required (e.g. $5, $7)
    const currencyMatch = (s, i) => { const m = s.slice(i).match(/^\$([\d,]+(?:\.\d+)?)(?=\s|$|\.|,|\?|!)/); return m ? m[0].length : 0; };
    let processed = t
      // Display math first: $$formula$$
      .replace(/\$\$([^$]+)\$\$/g, '<div class="math-display">$$$$$1$$</div>');
    // Inline: parse sequentially so $5 and $7 stay separate when mixed with $\frac{4}{5}$
    // Also handle LaTeX with trailing $ but no leading $ (e.g. " $\sqrt{x}$" or "\sqrt{x}$" after space) so space-after-formula doesn't break rendering
    let result = '';
    let i = 0;
    while (i < processed.length) {
      if (processed[i] === '$') {
        const rest = processed.slice(i + 1);
        const curLen = currencyMatch(processed, i);
        if (curLen > 0) {
          result += '<span class="tex2jax_ignore">' + processed.slice(i, i + curLen) + '</span>';
          i += curLen;
          continue;
        }
        const close = rest.indexOf('$');
        if (close === -1) { result += processed[i]; i++; continue; }
        const inner = rest.slice(0, close);
        if (looksLikeLatex(inner)) {
          if (debug) console.log('[MathRenderer] $...$ segment', { inner, isMath: true });
          result += '<span class="math-inline">$' + inner + '$</span>';
          i += 1 + close + 1;
          continue;
        }
        const literal = inner.endsWith('\\') ? inner.slice(0, -1) : inner;
        result += '<span class="tex2jax_ignore">$' + literal + '$</span>';
        i += 1 + close + 1;
        continue;
      }
      // LaTeX without leading $ (e.g. after space): "\frac{a}{b}$" or "\sqrt{x}$" -> treat as inline math so rendering stays correct
      if (processed[i] === '\\') {
        const nextDollar = processed.indexOf('$', i + 1);
        if (nextDollar !== -1) {
          const inner = processed.slice(i, nextDollar);
          if (looksLikeLatex(inner)) {
            if (debug) console.log('[MathRenderer] \\...$ segment (no leading $)', { inner, isMath: true });
            result += '<span class="math-inline">$' + inner + '$</span>';
            i = nextDollar + 1;
            continue;
          }
        }
      }
      result += processed[i];
      i++;
    }
    processed = result;
    // Do not escape backslashes globally - it breaks LaTeX inside math spans (e.g. \sqrt{6})
    if (debug) {
      console.log('[MathRenderer]', { raw: text, normalized: t, processedSnippet: processed.substring(0, 200), hasMathInline: /math-inline/.test(processed) });
    }
    return processed;
  }, [escapeHtml]);

  const processedContent = processContent(content);
  const hasMath = /math-inline|math-display/.test(processedContent);

  useEffect(() => {
    if (content !== lastContentRef.current) {
      lastContentRef.current = content;
    }
    if (!hasMath) return;

    const runTypeset = () => {
      if (containerRef.current && typeof window !== 'undefined' && window.MathJax) {
        window.MathJax.typesetPromise([containerRef.current]).catch((err) => {
          console.error('MathJax error:', err);
        });
      }
    };

    // Run typeset when content has math. Use multiple delays so formulas render when dialog first opens
    // (e.g. View Question dialog loads content async; MathJax script may also load async).
    const t1 = setTimeout(() => {
      if (!containerRef.current) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(runTypeset);
      });
    }, 100);
    const t2 = setTimeout(runTypeset, 500);
    const t3 = setTimeout(runTypeset, 1000);
    const t4 = setTimeout(runTypeset, 2000);

    // If MathJax isn't loaded yet (e.g. View dialog opened before script ready), retry until it is
    let pollId = null;
    if (typeof window !== 'undefined' && !window.MathJax) {
      pollId = setInterval(() => {
        if (window.MathJax && containerRef.current) {
          runTypeset();
          if (pollId) clearInterval(pollId);
        }
      }, 200);
    }

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      if (pollId) clearInterval(pollId);
    };
  }, [content, hasMath]);

  return (
    <Box ref={containerRef} sx={sx} role="text" aria-label="Mathematical content">
      <Typography
        variant={variant}
        component="div"
        dangerouslySetInnerHTML={{ __html: processedContent }}
        sx={{
          '& .math-inline': { display: 'inline', fontFamily: 'serif' },
          '& .math-display': { display: 'block', textAlign: 'center', margin: '1em 0', fontFamily: 'serif' },
          '& .math-inline + .math-inline': { marginLeft: '0.2em' },
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      />
    </Box>
  );
});

MathRenderer.displayName = 'MathRenderer';

export default MathRenderer;
